// Client side of Phase 5: scoped, limited, live watchers over pickup requests, runs and their
// stops, driver cash custody, empty-box transfers and the shipments that can go out on an own
// delivery run, plus the device heartbeat callable. All of these documents are written by the
// server; clients change them only through commands (04 §4.2, §4.5). Every query has a `where` on
// scope (branch or sector, or the parent document of a subcollection) and a `limit` (02 §2.3).
// The composite indexes each watcher needs are listed in its comment.
import {
  collection,
  collectionGroup,
  doc,
  limit,
  orderBy,
  query,
  where,
  type DocumentData,
  type Firestore,
  type QueryConstraint,
} from 'firebase/firestore';
import { httpsCallable, type Functions } from 'firebase/functions';
import { ulid } from 'ulid';
import { formatPrefixedId } from '@ecobox/domain';
import type {
  ReportDeviceInput,
  driverCustodyDocSchema,
  emptyBoxTransferDocSchema,
  geoSchema,
  pickupRequestDocSchema,
  runDocSchema,
  stopDocSchema,
} from '@ecobox/schema';
import { toConfigError } from './config';
import { toDoc, watchOne, watchRows, type Unsubscribe } from './docs';
import type { BoxRow, ShipmentRow } from './counter';
import { watchBoxesHere, type MovementScope } from './movement';

export type PickupRequestRow = (typeof pickupRequestDocSchema)['_output'];
export type RunRow = (typeof runDocSchema)['_output'];
export type StopRow = (typeof stopDocSchema)['_output'];
export type DriverCustodyRow = (typeof driverCustodyDocSchema)['_output'];
export type GeoPoint = (typeof geoSchema)['_output'];
export type EmptyBoxTransferRow = (typeof emptyBoxTransferDocSchema)['_output'];

export const PICKUP_LIST_LIMIT = 100;
export const RUN_LIST_LIMIT = 50;
/** A run carries at most 120 stops at planning (`planRun`); a few more may be added later. */
export const RUN_STOPS_LIMIT = 240;
/** The most runs whose stops one screen watches at once (the board and the runs list). */
export const MAX_WATCHED_RUNS = 30;
export const CUSTODY_LIST_LIMIT = 100;
export const TRANSFER_LIST_LIMIT = 50;
export const DRIVER_LIST_LIMIT = 100;
export const SHIPMENT_STOPS_LIMIT = 10;

/** Pickup requests a dispatcher still has to act on, or that are on their way. */
export const OPEN_PICKUP_STATUSES = ['requested', 'scheduled', 'assigned', 'en_route'] as const;
/** Pickup requests that are over. */
export const FINISHED_PICKUP_STATUSES = ['completed', 'failed', 'cancelled'] as const;
/** Runs that are not closed yet. */
export const OPEN_RUN_STATUSES = ['planned', 'assigned', 'active'] as const;
/**
 * Shipments of an own-delivery city that can still have boxes at the hub or on a van: from the
 * time they are on their way to the hub until every box is delivered.
 */
export const OWN_DELIVERY_STATUSES = [
  'in_transit',
  'arrived',
  'out_for_delivery',
  'partially_delivered',
] as const;

const scopeFilter = (scope: MovementScope): QueryConstraint =>
  'branchId' in scope
    ? where('branchId', '==', scope.branchId)
    : where('sectorId', '==', scope.sectorId);

const row =
  <T>(idField: string) =>
  (id: string, data: DocumentData): T =>
    toDoc<T>(id, data, idField);

// ---- new ids -------------------------------------------------------------------

/** A new `pur_…` id made on the device, so the request can be named before the server has it. */
export function newPickupRequestId(): string {
  return formatPrefixedId('pur', ulid());
}

/** A new `run_…` id made on the device. */
export function newRunId(): string {
  return formatPrefixedId('run', ulid());
}

/**
 * A new `xfr_…` id for an empty-box transfer. The prefix is not in the domain's list of
 * prefixes yet, so it is built here; the command's schema is the check (`xfr_` + 26 characters).
 */
export function newTransferId(): string {
  return `xfr_${ulid()}`;
}

// ---- pickup requests ---------------------------------------------------------------

/**
 * Pickup requests of a branch or country. With `statuses`, only those (no ordering, so no
 * composite index: the screen sorts). Without, the newest first. Index:
 * `pickupRequests (branchId | sectorId asc, createdAt desc)`.
 */
export function watchPickupRequests(
  db: Firestore,
  args: { scope: MovementScope; statuses?: readonly string[]; max?: number },
  onData: (rows: PickupRequestRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const constraints: QueryConstraint[] = [scopeFilter(args.scope)];
  if (args.statuses && args.statuses.length > 0) {
    constraints.push(where('status', 'in', [...args.statuses]));
  } else {
    constraints.push(orderBy('createdAt', 'desc'));
  }
  constraints.push(limit(args.max ?? PICKUP_LIST_LIMIT));
  return watchRows(
    query(collection(db, 'pickupRequests'), ...constraints),
    row<PickupRequestRow>('id'),
    onData,
    onError,
  );
}

// ---- runs and stops ----------------------------------------------------------------

/**
 * Runs of a branch or country. With `date`, that day's runs (equality filters only, no index).
 * With `statuses`, only runs in those states, whatever their day (no ordering, no index): the runs
 * still open from earlier days. Without either, the newest first. Index:
 * `runs (branchId | sectorId asc, date desc)`.
 */
export function watchRuns(
  db: Firestore,
  args: { scope: MovementScope; date?: string; statuses?: readonly string[]; max?: number },
  onData: (rows: RunRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const constraints: QueryConstraint[] = [scopeFilter(args.scope)];
  if (args.date) {
    constraints.push(where('date', '==', args.date));
  } else if (args.statuses && args.statuses.length > 0) {
    constraints.push(where('status', 'in', [...args.statuses]));
  } else {
    constraints.push(orderBy('date', 'desc'));
  }
  constraints.push(limit(args.max ?? RUN_LIST_LIMIT));
  return watchRows(
    query(collection(db, 'runs'), ...constraints),
    row<RunRow>('id'),
    onData,
    onError,
  );
}

/** One run, live. */
export function watchRun(
  db: Firestore,
  runId: string,
  onData: (row: RunRow | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return watchOne(doc(db, 'runs', runId), row<RunRow>('id'), onData, onError);
}

/**
 * The stops of one run, at most 240. Staff are let in by the branch written on each stop (a driver
 * by being the run's driver), and the rules can only prove that for a query that filters on it, so
 * a screen passes the run's `branchId`: equality filter only, no composite index, and the screen
 * puts the stops in route order itself. Without it (the driver's phone) the stops come in
 * `sequence` order.
 */
export function watchStopsOfRun(
  db: Firestore,
  args: { runId: string; branchId?: string; max?: number },
  onData: (rows: StopRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const max = Math.min(args.max ?? RUN_STOPS_LIMIT, RUN_STOPS_LIMIT);
  const q = args.branchId
    ? query(
        collection(db, 'runs', args.runId, 'stops'),
        where('branchId', '==', args.branchId),
        limit(max),
      )
    : query(collection(db, 'runs', args.runId, 'stops'), orderBy('sequence', 'asc'), limit(max));
  return watchRows(q, row<StopRow>('id'), (rows) => onData(rows.sort(bySequence)), onError);
}

const bySequence = (a: StopRow, b: StopRow): number =>
  a.sequence - b.sequence || a.id.localeCompare(b.id);

/**
 * The stops of several runs at once: one listener per run, at most 30 runs. Gives the latest rows
 * of every run, keyed by run id, each time one of them changes; nothing is given until every run
 * has answered once.
 */
export function watchStopsOfRuns(
  db: Firestore,
  runs: readonly { id: string; branchId: string }[],
  onData: (byRun: ReadonlyMap<string, StopRow[]>) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const unique = [...new Map(runs.map((r) => [r.id, r])).values()].slice(0, MAX_WATCHED_RUNS);
  const latest = new Map<string, StopRow[]>();
  if (unique.length === 0) {
    onData(latest);
    return () => undefined;
  }
  const stops = unique.map((run) =>
    watchStopsOfRun(
      db,
      { runId: run.id, branchId: run.branchId },
      (rows) => {
        latest.set(run.id, rows);
        if (latest.size === unique.length) onData(new Map(latest));
      },
      onError,
    ),
  );
  return () => stops.forEach((stop) => stop());
}

/**
 * Every stop that names one shipment, whichever run it was on (the shipment drawer's own-delivery
 * leg). A collection-group query on `stops`, scoped by the branch that runs the delivery (the hub
 * that serves the destination city, which every stop carries). Equality filters only. NEEDS the
 * server: a collection-group rule for `stops` and a collection-group index on `stops.shipmentId`.
 */
export function watchShipmentStops(
  db: Firestore,
  args: { branchId: string; shipmentId: string },
  onData: (rows: StopRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collectionGroup(db, 'stops'),
    where('branchId', '==', args.branchId),
    where('shipmentId', '==', args.shipmentId),
    limit(SHIPMENT_STOPS_LIMIT),
  );
  return watchRows(q, row<StopRow>('id'), onData, onError);
}

// ---- what can go out on a delivery run ---------------------------------------------------

/**
 * Shipments the hub delivers itself (coverage says `own`, served from this branch) that can still
 * have boxes waiting there. Equality filters plus an `in` filter: no composite index. At most 500.
 * ASSUMPTION for the rules: staff at the serving hub may read shipments whose
 * `destination.servingBranchId` is their branch (as for partner shipments, Phase 4).
 */
export function watchOwnDeliveryShipments(
  db: Firestore,
  args: { hubId: string; max?: number },
  onData: (rows: ShipmentRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'shipments'),
    where('destination.servingBranchId', '==', args.hubId),
    where('destination.mode', '==', 'own'),
    where('status', 'in', [...OWN_DELIVERY_STATUSES]),
    limit(Math.min(args.max ?? 500, 500)),
  );
  return watchRows(q, row<ShipmentRow>('trackingNo'), onData, onError);
}

/** The boxes at a hub and the own-delivery shipments of that hub, together. */
export interface DeliverableSource {
  boxes: BoxRow[];
  shipments: ShipmentRow[];
}

/**
 * What could go out on a delivery run from a hub: its boxes (at the branch) and its own-delivery
 * shipments. The planner joins them with the zones and the holds, and says why a shipment cannot
 * go. Nothing is given until both have answered; if the shipments cannot be read, the boxes are
 * still given (with no shipments) and the error is reported.
 */
export function watchDeliverableAtHub(
  db: Firestore,
  args: { branchId: string },
  onData: (source: DeliverableSource) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  let boxes: BoxRow[] | undefined;
  let shipments: ShipmentRow[] | undefined;
  const emit = (): void => {
    if (boxes && shipments) onData({ boxes, shipments });
  };
  const stopBoxes = watchBoxesHere(
    db,
    { branchId: args.branchId },
    (rows) => {
      boxes = rows;
      emit();
    },
    onError,
  );
  const stopShipments = watchOwnDeliveryShipments(
    db,
    { hubId: args.branchId },
    (rows) => {
      shipments = rows;
      emit();
    },
    (err) => {
      shipments ??= [];
      emit();
      onError(err);
    },
  );
  return () => {
    stopBoxes();
    stopShipments();
  };
}

// ---- driver cash -----------------------------------------------------------------------

/**
 * Cash drivers hold, one document per driver and branch (`driverCustody/{branchId}__{uid}`).
 * Equality filter only: the screen drops the ones that hold nothing. At most 100.
 */
export function watchDriverCustody(
  db: Firestore,
  args: { scope: MovementScope; max?: number },
  onData: (rows: DriverCustodyRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'driverCustody'),
    scopeFilter(args.scope),
    limit(args.max ?? CUSTODY_LIST_LIMIT),
  );
  return watchRows(q, row<DriverCustodyRow>('id'), onData, onError);
}

// ---- empty-box transfers ----------------------------------------------------------------

/**
 * `emptyBoxTransfers/{xfr_…}` a branch sent (`side: 'from'`) or is waiting for (`side: 'to'`),
 * newest first. A transfer is read where it is sent from and where it is going; the rules can
 * only prove the end the query filters on, so the sending side asks by `branchId` (the sending
 * branch, the same as `fromBranchId`) and the receiving side by `toBranchId`. Indexes:
 * `emptyBoxTransfers (branchId asc, sentAt desc)` and `emptyBoxTransfers (toBranchId asc, sentAt desc)`.
 */
export function watchEmptyBoxTransfers(
  db: Firestore,
  args: { branchId: string; side: 'from' | 'to'; max?: number },
  onData: (rows: EmptyBoxTransferRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'emptyBoxTransfers'),
    where(args.side === 'from' ? 'branchId' : 'toBranchId', '==', args.branchId),
    orderBy('sentAt', 'desc'),
    limit(args.max ?? TRANSFER_LIST_LIMIT),
  );
  return watchRows(q, row<EmptyBoxTransferRow>('id'), onData, onError);
}

// ---- drivers ----------------------------------------------------------------------------

export interface DriverRow {
  uid: string;
  name: string;
}

/**
 * The people who can be given a run at a branch, from `driverDirectory/{uid}` (written by the
 * staff functions, readable by any active staff member; `staff` itself is not). One equality
 * filter (`active == true`), at most 100, so no composite index; the branch is matched here:
 * `scopeAll`, the branch in `branchIds`, or the branch's country in `sectorIds` (07, 7.3).
 */
export function watchDriverDirectory(
  db: Firestore,
  args: { branchId: string; sectorId?: string | null; max?: number },
  onData: (rows: DriverRow[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'driverDirectory'),
    where('active', '==', true),
    limit(args.max ?? DRIVER_LIST_LIMIT),
  );
  return watchRows(
    q,
    (id, data) => {
      const branches = Array.isArray(data['branchIds']) ? (data['branchIds'] as unknown[]) : [];
      const sectors = Array.isArray(data['sectorIds']) ? (data['sectorIds'] as unknown[]) : [];
      const here =
        data['scopeAll'] === true ||
        branches.includes(args.branchId) ||
        (args.sectorId != null && sectors.includes(args.sectorId));
      if (!here) return null;
      const name = typeof data['name'] === 'string' ? data['name'] : '';
      return { uid: id, name: name || id };
    },
    onData,
    onError,
  );
}

// ---- reportDevice (callable) --------------------------------------------------------------

export type { ReportDeviceInput };

/**
 * A device says it is alive: how many commands wait on it and when it last reached the server.
 * The server writes `devices/{deviceId}` (with the caller's uid, the branch's sector and the time
 * it heard). `branchId` is the branch the person works at (04 §4.7, `reportDevice`).
 */
export async function callReportDevice(
  functions: Functions,
  input: ReportDeviceInput,
): Promise<void> {
  try {
    await httpsCallable<ReportDeviceInput, unknown>(functions, 'reportDevice')(input);
  } catch (err) {
    throw toConfigError(err);
  }
}
